/**
 * The network pages' pure logic: reading a link's evidence, the review queue's
 * keys, small list helpers. Kept free of React so it is tested on its own.
 */
import type { NetworkLinkEvidence, NetworkLinkMethod, NetworkLinkStatus } from '../../../common/models/network.model';
import { scoreTone, type ScoreTone } from '../../../components/design-system/common/ScoreChip';

export type Thresholds = { same: number; similar: number };

/**
 * SurfaceID's own thresholds come from its /health (via `stats.surfaceId`);
 * these are only the documented fallback for when the service is down — the
 * same 0.80 / 0.55 the API falls back to.
 */
export const DEFAULT_VISUAL_THRESHOLDS: Thresholds = { same: 0.8, similar: 0.55 };

/**
 * Name similarity and the matcher's overall score are not SurfaceID cosines,
 * so the model's thresholds do not apply to them. These bands only colour the
 * chips (they decide nothing): at 0.85 a name reads as the same product, under
 * 0.60 it is a guess.
 */
export const NAME_THRESHOLDS: Thresholds = { same: 0.85, similar: 0.6 };
export const OVERALL_THRESHOLDS: Thresholds = { same: 0.85, similar: 0.6 };

export const visualThresholds = (t?: Partial<Thresholds> | null): Thresholds => ({
    same: typeof t?.same === 'number' ? t.same : DEFAULT_VISUAL_THRESHOLDS.same,
    similar: typeof t?.similar === 'number' ? t.similar : DEFAULT_VISUAL_THRESHOLDS.similar,
});

export type SignalKey = 'ean' | 'ref' | 'name' | 'visual' | 'format' | 'brand' | 'colour';

export interface Signal {
    key: SignalKey;
    tone: ScoreTone;
    /** A 0..1 score, when the signal is one. */
    score?: number;
    /** A verdict word, when the signal is a comparison: agrees/differs/equal/different/unknown/match/none. */
    state?: 'agrees' | 'differs' | 'equal' | 'different' | 'unknown' | 'match' | 'none';
    /** The matched value itself (the EAN, the ref). */
    detail?: string;
}

const verdict = (v: string | undefined, yes: string, no: string): Pick<Signal, 'tone' | 'state'> =>
    v === yes ? { tone: 'good', state: yes as Signal['state'] } : v === no ? { tone: 'poor', state: no as Signal['state'] } : { tone: 'none', state: 'unknown' };

/**
 * Every signal the matcher recorded for a link, in the order a reviewer reads
 * them: identifiers first (an EAN or ref match is near-proof), then the name
 * and the picture, then the cross-checks that catch a near-miss (a 60×60 and
 * a 60×120 of the same tile are different SKUs).
 */
export function evidenceSignals(e: NetworkLinkEvidence | null | undefined, visual: Thresholds = DEFAULT_VISUAL_THRESHOLDS): Signal[] {
    const ev = e || {};
    return [
        ev.ean ? { key: 'ean', tone: 'good', state: 'match', detail: ev.ean } : { key: 'ean', tone: 'none', state: 'none' },
        ev.ref ? { key: 'ref', tone: 'good', state: 'match', detail: ev.ref } : { key: 'ref', tone: 'none', state: 'none' },
        { key: 'name', tone: scoreTone(ev.nameScore, NAME_THRESHOLDS), score: ev.nameScore },
        { key: 'visual', tone: scoreTone(ev.visualScore, visual), score: ev.visualScore },
        { key: 'format', ...verdict(ev.format, 'equal', 'different') },
        { key: 'brand', ...verdict(ev.brand, 'agrees', 'differs') },
        { key: 'colour', ...verdict(ev.colour, 'agrees', 'differs') },
    ];
}

export type ReviewAction = 'next' | 'prev' | 'confirm' | 'reject' | 'undo';

/**
 * The review queue's keyboard: J/↓ next, K/↑ previous, C/Enter confirm,
 * X/Backspace reject, U undo. Nothing while the person is typing (a filter, the
 * score box) or holding a modifier (Ctrl+C must still copy).
 */
export function reviewKeyAction(e: {
    key: string;
    target?: EventTarget | null;
    ctrlKey?: boolean;
    metaKey?: boolean;
    altKey?: boolean;
}): ReviewAction | null {
    if (e.ctrlKey || e.metaKey || e.altKey) return null;
    const el = e.target as HTMLElement | null;
    if (el && typeof el === 'object' && 'tagName' in el) {
        const tag = String(el.tagName || '').toUpperCase();
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable) return null;
        // Enter on a focused button presses that button; it must not also confirm.
        if (e.key === 'Enter' && (tag === 'BUTTON' || tag === 'A')) return null;
    }
    switch (e.key) {
        case 'j':
        case 'J':
        case 'ArrowDown':
            return 'next';
        case 'k':
        case 'K':
        case 'ArrowUp':
            return 'prev';
        case 'c':
        case 'C':
        case 'Enter':
            return 'confirm';
        case 'x':
        case 'X':
        case 'Backspace':
            return 'reject';
        case 'u':
        case 'U':
            return 'undo';
        default:
            return null;
    }
}

/** The next index after `from` whose item is still open, or `from` when none is. */
export function nextOpenIndex<T>(items: T[], from: number, isOpen: (item: T) => boolean): number {
    for (let i = from + 1; i < items.length; i++) if (isOpen(items[i])) return i;
    return from;
}

export function chunk<T>(items: T[], size: number): T[][] {
    if (size <= 0) return [items];
    const out: T[][] = [];
    for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
    return out;
}

/** 0.873 → "87%"; null/undefined → "—". */
export function formatShare(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) return '—';
    return `${Math.round(value * 100)}%`;
}

/** "a, b ,, c" → ["a", "b", "c"] (trimmed, empty and duplicate entries dropped, case-insensitively). */
export function splitList(text: string): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of String(text || '').split(/[,\n;]/)) {
        const v = raw.trim();
        if (!v || seen.has(v.toLowerCase())) continue;
        seen.add(v.toLowerCase());
        out.push(v);
    }
    return out;
}

/** "cement-tile" → "CEMENT_TILE": the i18n key of a vocabulary value. */
export const vocabKey = (value: string) => String(value || '').toUpperCase().replace(/[^A-Z0-9]+/g, '_');

const LINK_STATUSES: NetworkLinkStatus[] = ['suggested', 'confirmed', 'rejected'];
const LINK_METHODS: NetworkLinkMethod[] = ['ean', 'ref', 'name', 'visual', 'import', 'manual'];

/** The review queue's filters, as its URL carries them (`?status=suggested&minScore=0.9&page=2`). */
export interface ReviewFilters {
    status: NetworkLinkStatus;
    method: NetworkLinkMethod | '';
    vendorId: string;
    brandId: string;
    /** 0..1, like the API. */
    minScore?: number;
    q: string;
    /** 1-based, like the API. */
    page: number;
}

/**
 * Reads the filters from the query string. Anything unusable falls back to
 * the default (the open suggestions, first page) rather than to an empty queue.
 */
export function readReviewFilters(params: URLSearchParams): ReviewFilters {
    const status = params.get('status') as NetworkLinkStatus;
    const method = params.get('method') as NetworkLinkMethod;
    const min = params.get('minScore');
    const minScore = min !== null && min.trim() !== '' ? Number(min) : NaN;
    const page = Math.floor(Number(params.get('page')));
    return {
        status: LINK_STATUSES.includes(status) ? status : 'suggested',
        method: LINK_METHODS.includes(method) ? method : '',
        vendorId: params.get('vendorId') || '',
        brandId: params.get('brandId') || '',
        minScore: Number.isFinite(minScore) && minScore >= 0 && minScore <= 1 ? minScore : undefined,
        q: params.get('q') || '',
        page: Number.isFinite(page) && page >= 1 ? page : 1,
    };
}

/** The query string for a set of filters; defaults are left out so a plain queue has a plain URL. */
export function writeReviewFilters(f: ReviewFilters): URLSearchParams {
    const p = new URLSearchParams();
    if (f.status && f.status !== 'suggested') p.set('status', f.status);
    if (f.method) p.set('method', f.method);
    if (f.vendorId) p.set('vendorId', f.vendorId);
    if (f.brandId) p.set('brandId', f.brandId);
    if (f.minScore !== undefined) p.set('minScore', String(f.minScore));
    if (f.q) p.set('q', f.q);
    if (f.page > 1) p.set('page', String(f.page));
    return p;
}

/** "80" → 0.8 (the page speaks in whole percentages, the API in 0..1); empty or nonsense → undefined. */
export function percentToScore(v: string): number | undefined {
    if (String(v ?? '').trim() === '') return undefined;
    const n = Number(String(v).replace(',', '.'));
    // Rounded so "99.9" reads back as 0.999, not 0.9990000000000001 (which would rewrite the URL).
    return Number.isFinite(n) ? Math.round(Math.max(0, Math.min(100, n)) * 100) / 10000 : undefined;
}

/** 0.999 → "99.9"; undefined → "". */
export function scoreToPercent(v: number | undefined): string {
    if (v === undefined || v === null || Number.isNaN(v)) return '';
    return String(Math.round(v * 1000) / 10);
}
